/*

This provides an API for capturing analytics information.
This module will send calls to our internal analytics service `/break/event' as well as Google Tag Manager (GTM)

One "gotcha" is that our internal analytics service takes eevrything in a flat key/value map
whereas GTM items have a structure:
{
  break: {}
  user: {}
}

Usage:
    import analytics from "global/AnalyticsData"

    // these methods do NOT trigger an analytics call (they could probably be deprecated)
    analytics.setUser(user)
    analytics.setBreak(brk)
    analytics.setStep(step)

    // these methods DO trigger an analytics call
    analytics.trackEvent('event_name', {extra: 'thing'})
    analytics.stepEvent('step_start', step)
    analytics.breakEvent('break_start', brk)
    analytics.userEvent('login', user)

    

*/
import { sendEvent } from 'services/analytics'
import { getUtmParameters } from 'util/utm'

/**
 * Drop keys with no value before pushing.
 *
 * GTM has no notion of "absent" — a key present with `undefined` still resolves the
 * variable, so an event would report an empty language rather than no language, and a
 * report cannot tell the two apart.
 */
const omitEmpty = obj =>
  Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== ''),
  )

class AnalyticsData {
  constructor() {
    this._brk = { platform: '', title: '' }
    this._step = {}
    this._user = { isGuest: true, date_of_registration: '01/01/2020 12:00:00' }
    this._data = {
      isGuest: true,
    }
  }

  async setUser(user) {
    this._user = {
      uid: user.isLoggedIn ? user.id : undefined,
      age: user.isLoggedIn ? user.age : undefined,
      gender: user.isLoggedIn ? user.gender : undefined,
      state: user.isLoggedIn ? user.state : undefined,
      zip: user.isLoggedIn ? user.zip : undefined,
      isGuest: !user.isLoggedIn,
      date_of_registration: user.isLoggedIn
        ? user.registrationDate
        : '01/01/2020 12:00:00', // TODO: Big Query requires registration date. What should this default to?
    }

    const utms = await getUtmParameters()

    this._data = Object.assign({}, this._data, utms?.parameters, this._user)
  }

  setBreak(brk) {
    // console.log("Set Break", brk.permaLink)
    this._brk = {
      break_id: brk ? brk.id : undefined,
      permalink: brk ? brk.permaLink : undefined,
      break_type: brk ? brk.sponsoredType : undefined,
      title: brk ? brk.title : undefined,
      platform: brk ? brk.platform : undefined,
      step_total: brk && brk.steps ? brk.steps.length : undefined,
      lang: brk ? brk.lang : undefined,
    }

    this._data = Object.assign({}, this._data, this._brk)
  }

  setStep(step) {
    // console.log("Set Step", step.type)
    const data = {
      step_id: step ? step.id : undefined,
      step_type: step ? step.type : undefined,
      step_number: step ? step.position : undefined,
    }

    this._data = Object.assign({}, this._data, data)
  }

  merge(additionalData) {
    return Object.assign({}, this._data, additionalData)
  }

  get data() {
    return this._data
  }

  /*
  INTERNAL METHOD
    breakEvent, stepEvent, and userEvent all call this
  */
  _trackEvent(eventName, additionalData) {
    // Every warehouse event goes through here, and `_data.lang` is whatever language
    // the break was authored in, put there by setBreak. The language the reader is
    // actually on wins when we know it: a Spanish page can render a break stored as
    // en-US, and the reader is still reading Spanish. The GTM pushes already prefer it,
    // and so does warehouseEvent — this is the same rule for the step and break events,
    // which are most of the volume.
    const payload = this.merge(additionalData)
    if (this._language) payload.lang = this._language

    return sendEvent(eventName, payload)
      .then(result => {
        return new Promise((resolve, reject) => {
          resolve(result)
        })
      })
      .catch(e => {
        console.warn(e)
        return new Promise((resolve, reject) => {
          resolve()
        })
      })
  }

  /**
   * The `break` half of a GTM push, in the shape the container variables read
   * (`break.language`, `break.permalink`, ...). See the note at the top of this file:
   * our warehouse takes a flat map, GTM takes this nested one.
   */
  _breakLayer(extra = {}) {
    return omitEmpty({
      breakid: this._brk.break_id,
      permalink: this._brk.permalink,
      break_type: this._brk.break_type,
      platform: this._brk.platform,
      step_total: this._brk.step_total,
      language: this._language ?? this._brk.lang,
      ...extra,
    })
  }

  /** The `user` half, including whatever UTMs have been captured this session. */
  _userLayer(extra = {}) {
    const d = this._data ?? {}
    return omitEmpty({
      uid: this._user.uid,
      age: this._user.age,
      gender: this._user.gender,
      state: this._user.state,
      zip: this._user.zip,
      is_guest: this._user.isGuest,
      date_of_registration: this._user.date_of_registration,
      utm_source: d.utm_source,
      utm_medium: d.utm_medium,
      utm_campaign: d.utm_campaign,
      utm_content: d.utm_content,
      utm_term: d.utm_term,
      ...extra,
    })
  }

  /**
   * The language the visitor is actually reading, for sites that serve one page per
   * language under a URL prefix. A break carries its own `lang`, but that is the
   * language the break was authored in, which is not the same question.
   */
  setLanguage(language) {
    this._language = language || undefined
  }

  /**
   * Send an event to the warehouse (`/break/event` -> BigQuery), for surfaces that are
   * not a break or a step — a click on a page module, say.
   *
   * `is_guest` is coerced to a string on purpose. AnalyticsService.validateEvent rejects
   * anything that is not the string "true" or "false", and this class holds it as a
   * boolean. Sending the boolean is what BR-4106 was: events rejected at the door, the
   * errors destabilising the API, and the whole generic path switched off as a result.
   *
   * `lang` is what the API maps onto the BigQuery `language` column (dailybreak-api
   * PR #1158); without it every Celebrando event would report as English.
   */
  warehouseEvent(eventName, additionalData = {}) {
    if (!eventName) return Promise.resolve()

    const payload = omitEmpty({
      ...this._data,
      isGuest: String(this._user?.isGuest ?? true),
      lang: this._language ?? this._brk.lang,
      ...additionalData,
    })

    return sendEvent(eventName, payload)
  }

  /**
   * Merge captured UTMs into the shared data.
   *
   * `setUser` already does this, but it only runs for a signed-in visitor. Most traffic
   * on a campaign site is anonymous, and that is exactly the traffic a UTM is there to
   * attribute, so the parameters have to reach the layer without waiting for a login.
   */
  setUtms(parameters) {
    if (!parameters) return
    this._data = Object.assign({}, this._data, omitEmpty(parameters))
  }

  /*
    Note: use breakEvent, stepEvent, or userEvent if the event is based on a break, step, or user.
  */
  trackEvent(eventName, additionalData = {}) {
    // Until BR-5583 this pushed `{ event }` and nothing else, so every container
    // variable reading break.* or user.* resolved to undefined — including the language,
    // which is the whole point of reporting a bilingual site. `additionalData` was
    // already accepted here and thrown away.
    const { break: breakExtra, user: userExtra, ...rest } = additionalData ?? {}

    window &&
      window.dataLayer &&
      window.dataLayer.push({
        event: eventName,
        break: this._breakLayer(breakExtra),
        user: this._userLayer(userExtra),
        ...rest,
      })

    // BR-4106 - do not send these events to data warehouse
    // return this._trackEvent(eventName, additionalData)
    return new Promise((resolve, reject) => {
      resolve()
    })
  }

  async userEvent(eventName, user) {
    const utms = await getUtmParameters()

    window &&
      window.dataLayer &&
      window.dataLayer.push({
        event: eventName,
        user: {
          uid: user.id,
          age: user.age,
          date_of_registration: user.registrationDate,
          is_guest: user.guest,
          zip: user.zip,
          gender: user.gender,
          state: user.state,
          ...utms?.parameters,
        },
      })

    // BR-4106 - do not send these events to data warehouse
    // if (eventName !== 'registration') {
    //   return this._trackEvent(eventName)
    // }
    return new Promise((resolve, reject) => {
      resolve()
    })
  }
  /**
   * @typedef {Object} AnalyticsEventContext
   * @property {String} context_id
   * @property {String} context
   * @property {String} result_id
   * @property {String} result
   * @property {String} context_total for items in a list, this is the total number of items
   * @property {String} context_position for items in a list, this is the item's position, e.g. quiz question 2

   * 
   * @param {String} eventName 
   * @param {Object} step 
   * @param {AnalyticsEventContext} additionalData is context @see [confluence doc on event-context](https://thedailybreak.atlassian.net/wiki/spaces/D2/pages/1263304705/Events#Event-Context). 
   * 
   * Usage:
   ```
      analytics.stepEvent('step_interaction_pq_answer_selected', step, {
      context_id: questionId,
      context: question.text,
      result_id: answerId,
      result: answer.text,
      context_total: quiz.questions.length,
      context_position: state.position,
    })
   ```

   */
  // TODO: Change to the new implementation in other places
  async stepEvent(eventName, step, additionalData = {}) {
    try {
      this.setStep(step)
      const utms = await getUtmParameters()
      const payload = Object.assign({}, utms?.parameters, additionalData)

      if (this._brk.break_type === 'sponsored') {
        // step_start BQ evenet is handled server side when the UI calls /<step>/start
        // step_completion evenet is handled server side when the UI calls /<step>/complete
        // we still want a unified external api to triggering events
        // we still want to pass these events to GTM
        if (eventName !== 'step_start' && eventName !== 'step_completion') {
          this._trackEvent(eventName, payload)
        }

        window &&
          window.dataLayer &&
          window.dataLayer.push({
            event: eventName,
            break: {
              stepid: step ? step.id : undefined,
              step_type: step ? step.type : undefined,
              step_number: step ? step.position : undefined,
              // Same reason as breakEvent: the container reads `break.language`, and a
              // step belongs to whichever language page the reader is on.
              language: this._language ?? this._brk.lang,
              ...additionalData,
            },
          })
      }
    } catch (e) {
      console.warn(eventName, e)
    }
  }

  async stepEventNewUtm(eventName, step, additionalData = {}) {
    try {
      this.setStep(step)
      const utms = await getUtmParameters()
      const payload = Object.assign({}, utms?.parameters, additionalData)

      if (this._brk.break_type === 'sponsored') {
        // step_start BQ evenet is handled server side when the UI calls /<step>/start
        // step_completion evenet is handled server side when the UI calls /<step>/complete
        // we still want a unified external api to triggering events
        // we still want to pass these events to GTM
        if (eventName !== 'step_start' && eventName !== 'step_completion') {
          this._trackEvent(eventName, payload)
        }

        window &&
          window.dataLayer &&
          window.dataLayer.push({
            event: eventName,
            break: {
              stepid: step ? step.id : undefined,
              step_type: step ? step.type : undefined,
              step_number: step ? step.position : undefined,
              // Same reason as breakEvent: the container reads `break.language`, and a
              // step belongs to whichever language page the reader is on.
              language: this._language ?? this._brk.lang,
              ...additionalData,
            },
          })
      }
    } catch (e) {
      console.warn(eventName, e)
    }
  }

  async breakEvent(eventName, brk, additionalData) {
    this.setBreak(brk)
    const utms = await getUtmParameters()

    const payload = Object.assign({}, utms?.parameters, additionalData)

    if (this._brk.break_type === 'sponsored') {
      this._trackEvent(eventName, payload)

      window &&
        window.dataLayer &&
        window.dataLayer.push({
          event: eventName,
          break: {
            break_id: brk ? brk.id : undefined,
            permalink: brk ? brk.permalink : undefined,
            break_type: brk ? brk.sponsoredType : undefined,
            title: brk ? brk.title : undefined,
            platform: brk ? brk.platform : undefined,
            step_total: brk && brk.steps ? brk.steps.length : undefined,
            lang: brk ? brk.lang : undefined,
            // The container reads `break.language`, not `break.lang` — the older key is
            // kept so nothing that already depends on it breaks. Prefers the language
            // the reader is on over the one the break was authored in: a Spanish page
            // can embed a break stored as en-US, and the reader is still reading Spanish.
            language: this._language ?? (brk ? brk.lang : undefined),
          },
        })
    }
  }
}

export default new AnalyticsData()
